import { useMemo, useState } from 'react';
import { CaretDown, CaretRight } from 'shared/icons';
import { FormatInt } from '../../utils/format';
import Badge from '../ui/Badge';
import { Id, Mono } from '../ui/Mono';
import { BuildBlockCode } from './collector/collectorScan';
import { FormatBayWidth, OccupancyBadgeVariant, StatusBadgeVariant } from './locationFields';
import { BuildLocationsTree } from './locationsTree';

/**
 * CASCATA de localizações (r4, estilo pastas do Windows):
 * Rack ("Rack A") → Bloco ("A-01 · vão 2,50 m") → Andar → Posição, com os
 * endereços fixos (doca/fábrica/incidente) agrupados por tipo e os VIRTUAIS
 * por setor. Cada porta-pallet é um rack independente (letra) com blocos
 * sequenciais — não existe rua. (LPN → SKU entram como nível extra no M3.)
 *
 * Regras do dono (4ª rodada):
 *  - TUDO SEMPRE COLAPSADO ao abrir — rack fechado, bloco fechado, andar
 *    fechado, grupos fechados (nenhum nó nasce aberto);
 *  - PASTA ≠ ITEM: todo nó expansível tem cara de pasta (background + borda);
 *    a POSIÇÃO final é item, sem background;
 *  - clicar numa POSIÇÃO preenche o drawer permanente da página
 *    (`onSelectLocation`).
 */

const INDENT_PX = 22;

const FolderRow = ({ depth, isOpen, onToggle, title, meta }) => (
    <div className="py-0.5" style={{ paddingLeft: depth * INDENT_PX }}>
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            className="flex w-full items-center gap-2 rounded-lg border border-mono-200 bg-mono-50 px-2.5 py-1.5 text-left transition-colors hover:bg-mono-100 dark:border-mono-800 dark:bg-mono-900 dark:hover:bg-mono-800"
        >
            {isOpen ? (
                <CaretDown size={14} className="shrink-0 text-mono-500" />
            ) : (
                <CaretRight size={14} className="shrink-0 text-mono-500" />
            )}
            <span className="text-sm font-medium text-black dark:text-white">{title}</span>
            {meta ? <span className="text-xs text-mono-500 dark:text-mono-400">{meta}</span> : null}
        </button>
    </div>
);

// Item final (posição): SEM background/borda — visual deliberadamente ≠ pasta.
const LocationLeaf = ({ depth, row, label, onSelect, prefix = null }) => (
    <button
        type="button"
        onClick={() => onSelect?.(row)}
        style={{ paddingLeft: depth * INDENT_PX + 30 }}
        className="flex w-full flex-wrap items-center gap-2 rounded-lg py-1.5 pr-2 text-left transition-colors hover:bg-mono-50 dark:hover:bg-mono-900"
        title={label('wms.panel.whats_here')}
    >
        {prefix ? <Mono className="text-sm font-semibold text-black dark:text-white">{prefix}</Mono> : null}
        <Id value={row.code} className="text-xs text-mono-600 dark:text-mono-400" />
        <span className="ml-auto flex items-center gap-1.5">
            <Badge variant={StatusBadgeVariant(row.status)}>{label(`wms.location_status.${row.status}`)}</Badge>
            <Badge variant={OccupancyBadgeVariant(row.occupancy || 'FREE')}>
                {label(`wms.occupancy.${row.occupancy || 'FREE'}`)}
            </Badge>
        </span>
    </button>
);

const LocationsCascade = ({ rows, label, onSelectLocation }) => {
    const tree = useMemo(() => BuildLocationsTree(rows), [rows]);
    // r4: um único Set de nós abertos — TODAS as pastas (rack, bloco, ANDAR e
    // grupos) nascem fechadas; nada de andar aberto por padrão.
    const [expanded, setExpanded] = useState(() => new Set());

    const toggleNode = (key) => {
        setExpanded((previous) => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const positionsText = (count) =>
        `${FormatInt(count)} ${label(count === 1 ? 'wms.cascade.position_suffix' : 'wms.cascade.positions_suffix')}`;

    if (tree.totalCount === 0) {
        return (
            <div className="rounded-xl border border-mono-200 bg-white p-8 text-center text-sm text-mono-500 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400">
                {label('wms.cascade.empty')}
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-mono-200 bg-white p-2 dark:border-mono-800 dark:bg-mono-950">
            <p className="px-3 py-2 text-sm text-mono-600 dark:text-mono-400">
                <span className="font-semibold text-black dark:text-white">{FormatInt(tree.totalCount)}</span>{' '}
                localizações
            </p>

            {tree.racks.map((rack) => {
                const rackKey = `rack:${rack.rack}`;
                const rackOpen = expanded.has(rackKey);
                return (
                    <div key={rackKey}>
                        <FolderRow
                            depth={0}
                            isOpen={rackOpen}
                            onToggle={() => toggleNode(rackKey)}
                            title={`${label('wms.field.rack')} ${rack.rack}`}
                            meta={positionsText(rack.positionsCount)}
                        />
                        {rackOpen ? (
                            <div>
                                {rack.stagings.map((row) => (
                                    <LocationLeaf
                                        key={row.id || row.code}
                                        depth={1}
                                        row={row}
                                        label={label}
                                        onSelect={onSelectLocation}
                                    />
                                ))}
                                {rack.bays.map((bay) => {
                                    const bayKey = `${rackKey}:bay:${bay.bay}`;
                                    const bayOpen = expanded.has(bayKey);
                                    const bayText = FormatBayWidth(bay.bayWidth);
                                    return (
                                        <div key={bayKey}>
                                            {/* Título = o código da ÚNICA etiqueta física do rack (`A-01`). */}
                                            <FolderRow
                                                depth={1}
                                                isOpen={bayOpen}
                                                onToggle={() => toggleNode(bayKey)}
                                                title={`${BuildBlockCode(rack.rack, bay.bay)}${
                                                    bayText ? ` · ${label('wms.cascade.bay')} ${bayText} m` : ''
                                                }`}
                                                meta={positionsText(bay.positionsCount)}
                                            />
                                            {bayOpen
                                                ? bay.levels.map((level) => {
                                                      const levelKey = `${bayKey}:level:${level.level}`;
                                                      const levelOpen = expanded.has(levelKey);
                                                      return (
                                                          <div key={levelKey}>
                                                              <FolderRow
                                                                  depth={2}
                                                                  isOpen={levelOpen}
                                                                  onToggle={() => toggleNode(levelKey)}
                                                                  title={`A${level.level}`}
                                                                  meta={positionsText(level.positions.length)}
                                                              />
                                                              {levelOpen
                                                                  ? level.positions.map((row) => (
                                                                        <LocationLeaf
                                                                            key={row.id || row.code}
                                                                            depth={3}
                                                                            row={row}
                                                                            label={label}
                                                                            onSelect={onSelectLocation}
                                                                            prefix={`P${row.position ?? '?'}`}
                                                                        />
                                                                    ))
                                                                  : null}
                                                          </div>
                                                      );
                                                  })
                                                : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                );
            })}

            {tree.fixed.length > 0 ? (
                <div>
                    <FolderRow
                        depth={0}
                        isOpen={expanded.has('fixed')}
                        onToggle={() => toggleNode('fixed')}
                        title={label('wms.cascade.fixed_group')}
                        meta={positionsText(tree.fixed.reduce((sum, group) => sum + group.items.length, 0))}
                    />
                    {expanded.has('fixed')
                        ? tree.fixed.map((group) => {
                              const groupKey = `fixed:${group.type}`;
                              const groupOpen = expanded.has(groupKey);
                              return (
                                  <div key={groupKey}>
                                      <FolderRow
                                          depth={1}
                                          isOpen={groupOpen}
                                          onToggle={() => toggleNode(groupKey)}
                                          title={label(`wms.location_type.${group.type}`)}
                                          meta={positionsText(group.items.length)}
                                      />
                                      {groupOpen
                                          ? group.items.map((row) => (
                                                <LocationLeaf
                                                    key={row.id || row.code}
                                                    depth={2}
                                                    row={row}
                                                    label={label}
                                                    onSelect={onSelectLocation}
                                                />
                                            ))
                                          : null}
                                  </div>
                              );
                          })
                        : null}
                </div>
            ) : null}

            {tree.virtual.length > 0 ? (
                <div>
                    <FolderRow
                        depth={0}
                        isOpen={expanded.has('virtual')}
                        onToggle={() => toggleNode('virtual')}
                        title={label('wms.cascade.virtual_group')}
                        meta={positionsText(tree.virtual.reduce((sum, group) => sum + group.items.length, 0))}
                    />
                    {expanded.has('virtual')
                        ? tree.virtual.map((group) => {
                              const groupKey = `virtual:${group.sector ?? ''}`;
                              const groupOpen = expanded.has(groupKey);
                              return (
                                  <div key={groupKey}>
                                      <FolderRow
                                          depth={1}
                                          isOpen={groupOpen}
                                          onToggle={() => toggleNode(groupKey)}
                                          title={group.sector ?? label('wms.cascade.no_sector')}
                                          meta={positionsText(group.items.length)}
                                      />
                                      {groupOpen
                                          ? group.items.map((row) => (
                                                <LocationLeaf
                                                    key={row.id || row.code}
                                                    depth={2}
                                                    row={row}
                                                    label={label}
                                                    onSelect={onSelectLocation}
                                                />
                                            ))
                                          : null}
                                  </div>
                              );
                          })
                        : null}
                </div>
            ) : null}
        </div>
    );
};

export default LocationsCascade;
